import { ApolloClient, CombinedGraphQLErrors, InMemoryCache, split } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client/react';
import { createClient } from 'graphql-ws';
import { type ReactNode } from 'react';
import { environment } from '../../environments/environment';
import introspectionResult from '../../introspection-result';
import { useHostAuth } from './host-auth-context';
import { reportGraphQLError } from './report-graphql-error';

/**
 * One Apollo client for the whole remote.
 *
 * The remote mounts several React roots, not one: the routed app, Ask Derek,
 * and a root for each tab the Angular project view draws. Building the client
 * inside the provider gives every one of those its own cache and its own
 * WebSocket — a project with four tabs open would hold four sockets, answer
 * the same query four times, and show one tab a record another tab had already
 * updated. So the client is module state, built once and shared by every root.
 *
 * The cache being shared is also what makes moving between tabs quick: the
 * second visit renders from cache while the network catches up.
 */

interface HostAuthRef {
  getAccessToken: () => Promise<string>;
  isAuthenticated: boolean;
}

/**
 * The host auth the links read at request time.
 *
 * Read through this rather than captured in the closure because the roots
 * mount at different moments and the links must see whichever auth is current,
 * not whichever mounted first. Every root is handed the same host auth in
 * practice — it all comes from the one Angular `AuthService` — so which
 * provider wrote last does not matter.
 *
 * A container that is filled in rather than a binding that is replaced: the
 * provider below writes it while rendering, and rebinding a module variable
 * from inside a component is exactly what the compiler will not have.
 */
const hostAuth: HostAuthRef = {
  getAccessToken: () => Promise.reject(new Error('No auth provider')),
  isAuthenticated: false,
};

/**
 * Points the links at the auth of whichever root rendered last.
 *
 * Written while rendering rather than from an effect, and the rule against
 * touching module state from a component is disabled deliberately. Apollo
 * starts a query as the component asking for it renders, and effects run after
 * the whole tree has committed — so every query on a screen's first render
 * would go out before an effect here could fill this in, and the default below
 * rejects. Nothing renders from `hostAuth`; the links read it at request time,
 * long after this has settled.
 */
function useHostAuthForLinks(): void {
  const { getAccessToken, isAuthenticated } = useHostAuth();
  /* eslint-disable react-hooks/immutability */
  hostAuth.getAccessToken = getAccessToken;
  hostAuth.isAuthenticated = isAuthenticated;
  /* eslint-enable react-hooks/immutability */
}

const errorLink = onError(({ error, operation }) => {
  reportGraphQLError(error, operation.operationName);
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  } else {
    console.error(`[Network error]: ${error}`);
  }
});

function buildClient(): ApolloClient {
  const authLink = setContext(async (_, { headers }) => {
    if (!hostAuth.isAuthenticated) {
      return { headers: { ...headers, authorization: '' } };
    }
    const token = await hostAuth.getAccessToken();
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
      },
    };
  });

  const httpLink = new BatchHttpLink({
    uri: environment.graphql.uri,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: environment.graphql.wsUri,
      connectionParams: async () => ({
        authentication: await hostAuth.getAccessToken(),
      }),
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    errorLink.concat(authLink.concat(httpLink)),
  );

  return new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache({
      possibleTypes: introspectionResult.possibleTypes,
      typePolicies: {
        ProjectBoardCard: {
          fields: {
            comments: { merge: (_existing = [], incoming: any[]) => incoming },
            checklist: { merge: (_existing = [], incoming: any[]) => incoming },
            labels: { merge: (_existing = [], incoming: any[]) => incoming },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false,
      },
    },
  });
}

let client: ApolloClient | null = null;

/** The shared client, built on the first call. */
export function getApolloClient(): ApolloClient {
  if (!client) {
    client = buildClient();
  }
  return client;
}

/** Drops the shared client, so a spec starts from an empty cache. */
export function resetApolloClient(): void {
  client = null;
}

export function ApolloProvider({ children }: { children: ReactNode }) {
  // Written during render rather than in an effect: children fire queries as
  // they render, which happens before an effect here would have run, and those
  // queries would go out unauthenticated.
  useHostAuthForLinks();

  return <BaseApolloProvider client={getApolloClient()}>{children}</BaseApolloProvider>;
}
